import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Tooltip, Typography, CircularProgress, Stack, Box } from "@mui/material";
import copy from "copy-to-clipboard";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { UserType } from "../../../../utils/constants";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import { SaveButton, CancelButton } from "../../../common/Button";
import useAuth from "../../../../hooks/useAuth";

import colors from "../../../../utils/colors";

import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";
import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";

const Root = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 200px;
`;

const EMPTY_CERT_TEXT = "Nothing to show";

function SSLConfigurationGenerateOrigin({ open, siteUid, handleClose }) {
  const { userType } = useAuth();
  const { getOriginCert, setOriginCert } = useSSLConfig();
  const [cert, setCert] = useState(null);
  // `cert === null` was previously overloaded to mean both "still
  // loading" and "no cert has been generated for this site yet".
  // Because GET /sg_certs legitimately returns `null` for a fresh
  // site, the initial fetch would land in the "no cert" state but
  // render the loading spinner forever. Track loading separately so
  // the empty-state and in-flight-state can render differently.
  const [loading, setLoading] = useState(false);
  const [copyFullchainSuccess, setCopyFullchainSuccess] = useState(false);
  const [copyPrivkeySuccess, setCopyPrivkeySuccess] = useState(false);

  const handleCopyFullchain = (e) => {
    copy(cert?.fullchain || EMPTY_CERT_TEXT);
    setCopyFullchainSuccess(true);
  };

  const handleCopyPrivkey = (e) => {
    copy(cert?.privkey || EMPTY_CERT_TEXT);
    setCopyPrivkeySuccess(true);
  };

  const handleOnFullchainTooltipClose = () => {
    setTimeout(() => {
      setCopyFullchainSuccess(false);
    }, 100);
  };
  const handleOnPrivkeyTooltipClose = () => {
    setTimeout(() => {
      setCopyPrivkeySuccess(false);
    }, 100);
  };
  useEffect(() => {
    const getCert = async () => {
      setLoading(true);
      try {
        setCert(await getOriginCert(siteUid));
      } finally {
        setLoading(false);
      }
    };
    if (open === true) {
      setCert(null);
      getCert();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => {
      setCopyFullchainSuccess(false);
      setCopyPrivkeySuccess(false);
    };
  }, [siteUid, open, getOriginCert]);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      setCert(await setOriginCert(siteUid));
    } finally {
      setLoading(false);
    }
  };

  const hasCert = Boolean(cert && (cert.fullchain || cert.privkey));

  return (
    <Stack spacing={3} justifyContent={"space-between"} sx={{ height: "100%" }}>
      {loading ? (
        <Root>
          <CircularProgress color="primary" />
        </Root>
      ) : !hasCert ? (
        // Fresh site: GET returned null / an empty payload. Tell the
        // operator what they're looking at instead of a bare spinner.
        <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ py: 8, px: 4 }}>
          <Typography sx={{ fontWeight: "medium", fontSize: 18, lineHeight: "24px", color: colors.neutral[1000] }}>
            No origin certificate yet
          </Typography>
          <Typography sx={{ fontSize: 13, lineHeight: "20px", color: colors.neutral[700], textAlign: "center", maxWidth: 420 }}>
            Click <b>Generate</b> below to create a certificate signed by Layerd's origin CA. It secures connections between your server and Layerd AI and takes about a second to issue.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography
              sx={{
                fontWeight: "medium",
                fontSize: 18,
                lineHeight: "24px",
                color: colors.neutral[1000],
              }}
            >
              Generate Origin certificate
            </Typography>
            <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
              Generate a certificate to secure connections between your server and Layerd AI.
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            sx={{ width: "100%", height: 110, border: `solid 1px ${colors.neutral[300]}`, borderRadius: "8px", p: 4 }}
          >
            <Box sx={{ width: `calc(100% - 32px)`, height: 78 }}>
              <ReactPerfectScrollbar>
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                  {cert?.fullchain || EMPTY_CERT_TEXT}
                </Typography>
              </ReactPerfectScrollbar>
            </Box>
            <Tooltip
              title={copyFullchainSuccess ? "Copied!" : "Click to Copy"}
              leaveDelay={copyFullchainSuccess ? 500 : 200}
              onClose={handleOnFullchainTooltipClose}
            >
              <Box onClick={handleCopyFullchain} sx={{ cursor: "pointer" }}>
                <CopyIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Tooltip>
          </Stack>
          <Stack
            direction={"row"}
            alignItems={"flex-start"}
            justifyContent={"space-between"}
            sx={{ width: "100%", height: 110, border: `solid 1px ${colors.neutral[300]}`, borderRadius: "8px", p: 4 }}
          >
            <Box sx={{ width: `calc(100% - 32px)`, height: 78 }}>
              <ReactPerfectScrollbar>
                <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                  {cert?.privkey || EMPTY_CERT_TEXT}
                </Typography>
              </ReactPerfectScrollbar>
            </Box>
            <Tooltip
              title={copyPrivkeySuccess ? "Copied!" : "Click to Copy"}
              leaveDelay={copyPrivkeySuccess ? 500 : 200}
              onClose={handleOnPrivkeyTooltipClose}
            >
              <Box onClick={handleCopyPrivkey} sx={{ cursor: "pointer" }}>
                <CopyIcon style={{ color: colors.neutral[600] }} />
              </Box>
            </Tooltip>
          </Stack>
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              minHeight: 48,
              p: 2,
              borderRadius: "8px",
              backgroundColor: colors.primary["alpha"],
              border: `solid 1px ${colors.primary[300]}`,
            }}
          >
            <Box sx={{ width: 24, height: 24 }}>
              <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
            </Box>
            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
              You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
            </Typography>
          </Stack>
        </Stack>
      )}
      <Stack direction={"row"} alignItems={"center"} spacing={3}>
        {userType !== UserType.ORG_USER ? (
          <SaveButton
            onClick={handleGenerate}
            label={hasCert ? "Regenerate" : "Generate"}
            loading={loading}
            disabled={loading}
          />
        ) : (
          <></>
        )}
        <CancelButton onClick={handleClose} />
      </Stack>
    </Stack>
  );
}

export default SSLConfigurationGenerateOrigin;
